// Per-widget CSV download helpers for AI Chat Dashboards.
//
// Simpler than a multi-sheet workbook (an earlier version of this file
// built one .xlsx per dashboard via exceljs, with 2 sheets per widget —
// dropped per user feedback in favor of this): each widget gets its own
// small "Download data (.csv)" / "Download query (.sql)" pair, rendered
// inline on the widget card. No new dependency — plain CSV is just
// string-joining with correct quoting, no library needed. Two small
// files per widget instead of one big workbook; simpler code, and lets
// a user grab just the one widget they care about instead of always
// getting everything.

// RFC 4180-ish CSV quoting: wrap a field in double quotes if it contains
// a comma, double quote, or newline; escape any interior double quote by
// doubling it. Everything else passes through unquoted.
function csvField(value) {
  if (value === null || value === undefined) return '';
  const str = typeof value === 'number' ? String(value) : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(columns, rows) {
  const lines = [columns.map(csvField).join(',')];
  rows.forEach((row) => {
    lines.push(row.map(csvField).join(','));
  });
  // CRLF line endings — the traditional CSV convention, and what makes
  // Excel/Numbers on both Mac and Windows recognize the file cleanly
  // without a "this doesn't look like a delimited file" prompt.
  return lines.join('\r\n');
}

function triggerBlobDownload(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function safeFilenameBase(title, fallback) {
  return (title || fallback).replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_') || fallback;
}

/**
 * Download one widget's result rows as a .csv file.
 * @param {object} widget - { title, columns, rows, ok, error }
 */
export function downloadWidgetDataCsv(widget) {
  const base = safeFilenameBase(widget.title, 'widget');
  if (!widget.ok) {
    triggerBlobDownload(`Error\r\n${csvField(widget.error || 'Query failed.')}`, `${base}_error.csv`, 'text/csv;charset=utf-8;');
    return;
  }
  const columns = widget.columns || [];
  const rows = widget.rows || [];
  if (columns.length === 0) {
    triggerBlobDownload('No columns returned.', `${base}_data.csv`, 'text/csv;charset=utf-8;');
    return;
  }
  triggerBlobDownload(buildCsv(columns, rows), `${base}_data.csv`, 'text/csv;charset=utf-8;');
}

/**
 * Download the exact SQL that produced a widget's data, as a plain .sql
 * text file — not CSV, since a query is one block of text, not tabular
 * data. Kept in this module alongside the data download since both are
 * triggered from the same widget-level download menu.
 */
export function downloadWidgetQuery(widget) {
  const base = safeFilenameBase(widget.title, 'widget');
  triggerBlobDownload(widget.sql || '', `${base}_query.sql`, 'text/plain;charset=utf-8;');
}
